import { ComponentProps, useMemo } from "react"

import { twMerge } from "tailwind-merge"

import { useFieldContext } from "./FieldProvider"
import { PathBuilder } from "~/src/lib/iname"
import { dataFlag } from "~/src/lib/jsx"

export type InlineInputProps = { name?: PathBuilder | string; errored?: boolean } & Omit<
  ComponentProps<"input">,
  "name"
>

/**
 * A Beekit skin of an inlinable version of an input element.
 */
export function InlineInput(props: InlineInputProps) {
  const { name: name_, id: id_, errored: errored_, type, className, onChange, ...restProps } = props

  const fieldContext = useFieldContext()
  const id = useMemo(() => id_ ?? fieldContext?.inputId, [id_, fieldContext?.inputId])
  const name = useMemo(() => name_?.toString() ?? fieldContext?.name, [name_, fieldContext?.name])
  const errored = useMemo(() => errored_ ?? fieldContext?.hasErrors, [errored_, fieldContext?.hasErrors])

  return (
    <input
      id={id}
      name={name?.toString()}
      type={type}
      onChange={(event) => {
        fieldContext?.descendentChange?.()
        onChange?.(event)
      }}
      className={twMerge(
        "inline border-0 border-b-2 border-gray-300 bg-gray-100 px-2 pb-0 pt-0.5 transition",
        "focus:border-gray-900 focus:ring-0",
        "disabled:cursor-not-allowed disabled:bg-gray-100",
        type === "number" &&
          "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
        "data-[errored]:border-red-600 focus:data-[errored]:border-gray-900",
        className
      )}
      {...dataFlag(errored, "errored")}
      {...restProps}
    />
  )
}
